import { useConfigEditor, SaveBar, Field, Section } from "../../lib/admin-shared";

const PRESETS = [
    { name: "Default Earth", primary: "205 40% 40%", secondary: "140 25% 42%", accent: "200 50% 90%", background: "40 30% 96%", footer_bg: "210 35% 18%" },
    { name: "Soft Green", primary: "150 35% 35%", secondary: "200 35% 45%", accent: "150 40% 90%", background: "60 25% 96%", footer_bg: "150 30% 18%" },
    { name: "Sahel Sand", primary: "30 50% 38%", secondary: "190 30% 40%", accent: "35 60% 90%", background: "40 40% 96%", footer_bg: "30 30% 20%" },
    { name: "Calm Sky", primary: "210 50% 45%", secondary: "180 35% 42%", accent: "200 55% 92%", background: "210 30% 97%", footer_bg: "210 40% 22%" },
    { name: "Warm Clay", primary: "15 55% 45%", secondary: "120 25% 40%", accent: "20 60% 92%", background: "30 35% 96%", footer_bg: "15 35% 20%" },
    { name: "Deep Forest", primary: "165 45% 30%", secondary: "30 50% 40%", accent: "160 40% 88%", background: "60 20% 96%", footer_bg: "165 40% 15%" },
];

const Swatch = ({ hsl, label }) => (
    <div className="flex items-center gap-2">
        <div className="h-5 w-5 rounded-sm border border-border" style={{ background: `hsl(${hsl})` }} />
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
    </div>
);

const ColorField = ({ label, value, onChange, testid }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm border border-border" style={{ background: `hsl(${value || "0 0% 100%"})` }} />
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder='e.g. "205 40% 40%"' data-testid={testid}
                className="flex-1 px-3 py-2 rounded-sm border border-border bg-background text-sm font-mono" />
        </div>
    </label>
);

export default function ThemeEditor() {
    const { cfg, set, save, saving, saved, loading } = useConfigEditor((data) => {
        // Triggering a global reload helps tailwind utilities pick new vars
        // We rely on SiteConfigContext to apply CSS variables — no reload needed.
        return data;
    });
    if (loading || !cfg) return <div className="text-muted-foreground">Loading…</div>;

    const applyPreset = (p) => {
        save({
            color_primary: p.primary,
            color_secondary: p.secondary,
            color_accent: p.accent,
            color_background: p.background,
            color_footer_bg: p.footer_bg,
        });
    };

    return (
        <div className="max-w-5xl space-y-8" data-testid="theme-editor">
            <div>
                <h1 className="font-display text-4xl mb-2">Theme & Appearance</h1>
                <p className="text-muted-foreground">Quick color presets, fonts, and photo animations. Changes apply site-wide immediately.</p>
            </div>

            <Section title="Quick palettes" description="One-click apply a complete color palette. Fine-tune individual colors below.">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="palette-presets">
                    {PRESETS.map((p) => (
                        <button key={p.name} onClick={() => applyPreset(p)}
                            className="text-left p-4 rounded-sm border border-border hover:border-primary/60 bg-card transition-colors group"
                            data-testid={`palette-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                            <div className="flex items-center gap-1.5 mb-3">
                                {[p.background, p.primary, p.secondary, p.accent, p.footer_bg].map((c, i) => (
                                    <div key={i} className="h-6 w-6 rounded-sm border border-border/40" style={{ background: `hsl(${c})` }} />
                                ))}
                            </div>
                            <div className="text-sm font-medium">{p.name}</div>
                            <div className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Click to apply →</div>
                        </button>
                    ))}
                </div>
            </Section>

            <Section title="Colors (HSL)" description='Use the HSL format: "hue saturation% lightness%" e.g. "205 40% 40%".'>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <ColorField label="Background" value={cfg.color_background} onChange={(v) => set("color_background", v)} testid="t-bg" />
                    <ColorField label="Foreground (text)" value={cfg.color_foreground} onChange={(v) => set("color_foreground", v)} testid="t-fg" />
                    <ColorField label="Primary" value={cfg.color_primary} onChange={(v) => set("color_primary", v)} testid="t-primary" />
                    <ColorField label="Secondary" value={cfg.color_secondary} onChange={(v) => set("color_secondary", v)} testid="t-secondary" />
                    <ColorField label="Accent" value={cfg.color_accent} onChange={(v) => set("color_accent", v)} testid="t-accent" />
                    <ColorField label="Muted" value={cfg.color_muted} onChange={(v) => set("color_muted", v)} testid="t-muted" />
                    <ColorField label="Border" value={cfg.color_border} onChange={(v) => set("color_border", v)} testid="t-border" />
                    <ColorField label="Footer background" value={cfg.color_footer_bg} onChange={(v) => set("color_footer_bg", v)} testid="t-footer-bg" />
                    <ColorField label="Footer text" value={cfg.color_footer_text} onChange={(v) => set("color_footer_text", v)} testid="t-footer-text" />
                </div>
                <div className="border-t border-border pt-5 flex flex-wrap gap-6">
                    <Swatch hsl={cfg.color_primary} label="Primary" />
                    <Swatch hsl={cfg.color_secondary} label="Secondary" />
                    <Swatch hsl={cfg.color_accent} label="Accent" />
                    <Swatch hsl={cfg.color_background} label="Background" />
                </div>
            </Section>

            <Section title="Typography">
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Heading font family" value={cfg.font_heading} onChange={(v) => set("font_heading", v)} testid="t-font-heading" />
                    <Field label="Body font family" value={cfg.font_body} onChange={(v) => set("font_body", v)} testid="t-font-body" />
                </div>
                <Field label="Google Fonts URL" value={cfg.font_google_url} onChange={(v) => set("font_google_url", v)} testid="t-font-url" placeholder="https://fonts.googleapis.com/css2?family=…" />
            </Section>

            <Section title="Photo animations">
                <div className="grid sm:grid-cols-2 gap-5">
                    <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Animation type</span>
                        <select value={cfg.animation_type || "fade"} onChange={(e) => set("animation_type", e.target.value)}
                            className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="t-anim-type">
                            <option value="fade">Fade</option>
                            <option value="slide">Slide</option>
                            <option value="zoom">Zoom</option>
                            <option value="none">None</option>
                        </select>
                    </label>
                    <Field label="Duration (ms)" type="number" value={cfg.animation_duration} onChange={(v) => set("animation_duration", parseInt(v) || 700)} testid="t-anim-duration" />
                </div>
            </Section>

            <SaveBar onSave={save} saving={saving} saved={saved} />
        </div>
    );
}
